import { createRouter, createWebHistory } from 'vue-router'
import HomePage from '../pages/HomePage.vue'
import JobsPage from '../pages/JobsPage.vue'
import JobSeoPage from '../pages/JobSeoPage.vue'
import ServiceSlugPage from '../pages/ServiceSlugPage.vue'
import HowItWorksPage from '../pages/HowItWorksPage.vue'
import ResourcePage from '../pages/ResourcePage.vue'
import InsightArticlePage from '../pages/InsightArticlePage.vue'
import HubPage from '../pages/HubPage.vue'
import PrivacyPolicy from '../components/PrivacyPolicy.vue'
import LinkedInPrivacyPolicy from '../components/LinkedInPrivacyPolicy.vue'
import TermsOfService from '../components/TermsOfService.vue'
import CookiePolicy from '../components/CookiePolicy.vue'
import { getJobPage } from '../config/jobPages'
import {
  getServiceLocation,
  serviceLocations,
  locationRedirects,
} from '../config/locations'
import { getCommercialService, commercialServices } from '../config/commercialServices'
import { howItWorksPages, getHowItWorksPage } from '../config/howItWorks'
import { resourcePages, getResourcePage } from '../config/resources'
import { b2bArticles, getB2bArticle } from '../config/b2bArticles'

const servicesHubItems = [
  ...commercialServices.map((p) => ({
    to: `/services/${p.slug}`,
    label: p.h1,
    hint: p.keyword,
  })),
  ...serviceLocations.map((l) => ({
    to: `/services/${l.slug}`,
    label: `${l.name} (district)`,
    hint: l.aliases.join(' / '),
  })),
]

const routes = [
  { path: '/', name: 'home', component: HomePage },
  {
    path: '/services',
    name: 'services-hub',
    component: HubPage,
    props: {
      title: 'Services',
      seoTitle: 'Phlebotomy & Corporate Camp Services Kerala | Homvisit',
      path: '/services',
      intro:
        'Service-first pages for buyers — plus district coverage across Kerala. Start with what you need, then refine by location.',
      description:
        'Browse Homvisit services in Kerala: phlebotomy teams, corporate health camps, mobile collection, lab partnership, and all 14 district pages.',
      items: servicesHubItems,
    },
  },
  { path: '/services/kerala', redirect: '/services/phlebotomy-services-kerala' },
  ...Object.entries(locationRedirects).map(([from, to]) => ({
    path: `/services/${from}`,
    redirect: `/services/${to}`,
  })),
  {
    path: '/services/:slug',
    name: 'service-slug',
    component: ServiceSlugPage,
    beforeEnter: (to, _from, next) => {
      const slug = to.params.slug
      if (locationRedirects[slug]) {
        return next({ path: `/services/${locationRedirects[slug]}`, replace: true })
      }
      if (getCommercialService(slug) || getServiceLocation(slug)) next()
      else next({ name: 'services-hub' })
    },
  },
  {
    path: '/how-it-works',
    name: 'how-hub',
    component: HubPage,
    props: {
      title: 'How it works',
      seoTitle: 'How Homvisit Camps & Phlebotomy Work | Kerala Guide',
      path: '/how-it-works',
      intro: 'Process pages for camps, home collection, transport, quality, and training.',
      description:
        'See how Homvisit runs corporate medical camps, home collection, sample transport, cold chain, and phlebotomist training across Kerala.',
      items: howItWorksPages.map((p) => ({
        to: `/how-it-works/${p.slug}`,
        label: p.h1,
        hint: 'Process guide',
      })),
    },
  },
  {
    path: '/how-it-works/:slug',
    name: 'how-page',
    component: HowItWorksPage,
    beforeEnter: (to, _from, next) => {
      if (!getHowItWorksPage(to.params.slug)) next({ name: 'how-hub' })
      else next()
    },
  },
  {
    path: '/resources',
    name: 'resources-hub',
    component: HubPage,
    props: {
      title: 'Resources',
      seoTitle: 'Camp Checklists, SOPs & Guides | Homvisit Resources',
      path: '/resources',
      intro: 'Checklists, SOPs, and guides that build topical authority for buyers and partners.',
      description:
        'Homvisit resources for buyers and partners: NABL notes, corporate camp checklists, sample collection SOPs, screening guides, and FAQs.',
      items: resourcePages.map((p) => ({
        to: `/resources/${p.slug}`,
        label: p.h1,
        hint: 'Resource',
      })),
    },
  },
  {
    path: '/resources/:slug',
    name: 'resource-page',
    component: ResourcePage,
    beforeEnter: (to, _from, next) => {
      if (!getResourcePage(to.params.slug)) next({ name: 'resources-hub' })
      else next()
    },
  },
  {
    path: '/insights',
    name: 'insights-hub',
    component: HubPage,
    props: {
      title: 'Insights',
      seoTitle: 'B2B Phlebotomy Insights for Kerala Buyers | Homvisit',
      path: '/insights',
      intro:
        'B2B articles for labs, hospitals, and HR teams — the people who hire collection partners.',
      description:
        'Homvisit B2B insights on outsourcing phlebotomy, planning corporate camps, cold-chain integrity, and screening programs for labs and HR in Kerala.',
      items: b2bArticles.map((p) => ({
        to: `/insights/${p.slug}`,
        label: p.h1,
        hint: 'B2B guide',
      })),
    },
  },
  {
    path: '/insights/:slug',
    name: 'insight-page',
    component: InsightArticlePage,
    beforeEnter: (to, _from, next) => {
      if (!getB2bArticle(to.params.slug)) next({ name: 'insights-hub' })
      else next()
    },
  },
  { path: '/jobs', name: 'jobs', component: JobsPage },
  {
    path: '/jobs/:slug',
    name: 'job-seo',
    component: JobSeoPage,
    beforeEnter: (to, _from, next) => {
      if (!getJobPage(to.params.slug)) next({ name: 'jobs' })
      else next()
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: PrivacyPolicy,
    props: { isPage: true },
  },
  {
    path: '/linkedin-privacy-policy',
    name: 'linkedin-privacy-policy',
    component: LinkedInPrivacyPolicy,
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: TermsOfService,
    props: { isPage: true },
  },
  {
    path: '/cookie-policy',
    name: 'cookie-policy',
    component: CookiePolicy,
    props: { isPage: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

export default router
